import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { initBugReportBuffer, isBugReportEnabled } from './lib/bugReportBuffer'
import { isTimeOverrideEnabled } from './lib/timeOverride'
import { popPendingAuth, isImpersonatingNow } from './lib/impersonation'
import { useUserStore } from './store/useUserStore'
import type { AuthData } from './types/api/auth'

initBugReportBuffer()

/**
 * Announce hidden QA/dev-tool shortcuts in the console. On UAT these tools have
 * no visible affordance, so the console is how a tester discovers them. Silent
 * in environments where nothing is enabled (e.g. production).
 */
function logDevToolShortcuts(): void {
  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.userAgent)
  const combo = isMac ? '⌘⇧' : 'Ctrl+Shift+'
  const shortcuts: string[] = []
  if (isTimeOverrideEnabled()) shortcuts.push(`${combo}K — Time Override`)
  if (isBugReportEnabled()) shortcuts.push(`${combo}B — Bug Reporter`)
  if (shortcuts.length) {
    console.info(
      `%c[dev tools]%c ${shortcuts.join('   •   ')}`,
      'font-weight:bold;color:#3C3295',
      'color:inherit'
    )
  }
}

logDevToolShortcuts()

// Hydrate an impersonation session if the bootstrap page parked one for us.
// useUserStore was constructed with sessionStorage backing (see store init)
// because isImpersonatingNow() returned true, so login() persists correctly.
if (isImpersonatingNow()) {
  const pending = popPendingAuth() as AuthData | null
  if (pending) {
    useUserStore.getState().login(pending)
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
